import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Award, FileCheck, FlaskConical, TrendingUp, Download, Shield, 
  Building2, FileText, Calendar, MapPin, ExternalLink, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';
import { FlagImg } from '../components/FlagImg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const qualityBlocks = [
  { icon: Shield, titleKey: 'qualityPolicy', descKey: 'qualityPolicyDesc' },
  { icon: FileCheck, titleKey: 'controlTraceability', descKey: 'controlTraceabilityDesc' },
  { icon: FlaskConical, titleKey: 'testingLabs', descKey: 'testingLabsDesc' },
  { icon: TrendingUp, titleKey: 'continuousImprovement', descKey: 'continuousImprovementDesc' },
];

// Certification body logos
const CertLogo = ({ type }) => {
  const logos = {
    aenor: { bg: 'bg-blue-900', text: 'AENOR' },
    bqa: { bg: 'bg-blue-800', text: 'BQA' },
    procertus: { bg: 'bg-amber-700', text: 'BENOR' },
    mpa: { bg: 'bg-red-800', text: 'MPA NRW' },
    afcab: { bg: 'bg-blue-600', text: 'NF' },
  };
  const logo = logos[type] || { bg: 'bg-slate-700', text: 'CERT' };
  return (
    <div className={`w-16 h-16 ${logo.bg} flex items-center justify-center text-white font-heading font-bold text-xs`}>
      {logo.text}
    </div>
  );
};

// Scheme badge colors
const getSchemeBadgeColor = (scheme) => {
  const colors = {
    'BENOR': 'bg-amber-100 text-amber-800 border-amber-300',
    'MPA': 'bg-red-100 text-red-800 border-red-300',
    'NF': 'bg-blue-100 text-blue-800 border-blue-300',
    'KOMO': 'bg-orange-100 text-orange-800 border-orange-300',
    'Spanish': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'ISO': 'bg-green-100 text-green-800 border-green-300',
  };
  return colors[scheme] || 'bg-slate-100 text-slate-800 border-slate-300';
};

// Plant Section Component
const PlantSection = ({ plant, plantName, plantLocation, country, flag, certificates, language, t }) => {
  const [expanded, setExpanded] = useState(true);
  
  const managementCerts = certificates.filter(c => c.type === 'management');
  const productCerts = certificates.filter(c => c.type === 'product');
  
  // Group product certs by scheme
  const productCertsByScheme = productCerts.reduce((acc, cert) => {
    const scheme = cert.scheme || 'Other';
    if (!acc[scheme]) acc[scheme] = [];
    acc[scheme].push(cert);
    return acc;
  }, {});

  const getDescription = (descObj) => {
    if (typeof descObj === 'string') return descObj;
    return descObj?.[language] || descObj?.['en'] || descObj?.['es'] || '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('undefined');
    const locales = {
      es: 'es-ES',
      en: 'en-GB',
      fr: 'fr-FR',
      de: 'de-DE',
      nl: 'nl-NL'
    };
    return new Date(dateStr).toLocaleDateString(locales[language] || 'en-GB');
  };

  const getText = (key) => {
    const texts = {
      managementSystem: { en: 'Management System Certificates', es: 'Certificados del Sistema de Gestión', fr: 'Certificats de Système de Management', de: 'Managementsystem-Zertifikate', nl: 'Managementsysteem Certificaten' },
      productCertifications: { en: 'Product Certifications', es: 'Certificaciones de Producto', fr: 'Certifications de Produit', de: 'Produktzertifizierungen', nl: 'Productcertificeringen' },
      issuingBody: { en: 'Issuing Body', es: 'Organismo Emisor', fr: 'Organisme Émetteur', de: 'Ausstellende Stelle', nl: 'Uitgevende Instantie' },
      certNumber: { en: 'Certificate No.', es: 'Nº Certificado', fr: 'N° Certificat', de: 'Zertifikat-Nr.', nl: 'Certificaatnr.' },
      scope: { en: 'Scope', es: 'Alcance', fr: 'Champ', de: 'Geltungsbereich', nl: 'Scope' },
      validUntil: { en: 'Valid until', es: 'Válido hasta', fr: 'Valide jusqu\'au', de: 'Gültig bis', nl: 'Geldig tot' },
      standards: { en: 'Standards', es: 'Normas', fr: 'Normes', de: 'Normen', nl: 'Normen' },
      downloadPdf: { en: 'Download PDF', es: 'Descargar PDF', fr: 'Télécharger PDF', de: 'PDF Herunterladen', nl: 'Download PDF' },
      viewCertificate: { en: 'View Certificate', es: 'Ver Certificado', fr: 'Voir Certificat', de: 'Zertifikat Anzeigen', nl: 'Bekijk Certificaat' },
      productionPlant: { en: 'Production Plant', es: 'Planta de Producción', fr: 'Usine de Production', de: 'Produktionswerk', nl: 'Productiefabriek' },
    };
    return texts[key]?.[language] || texts[key]?.['en'] || key;
  };

  return (
    <div className="border border-slate-200 bg-white mb-8" data-testid={`plant-section-${plant}`}>
      {/* Plant Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-5 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid={`plant-toggle-${plant}`}
      >
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-slate-900 flex items-center justify-center text-white">
            <Building2 className="h-6 w-6" />
          </div>
          <div className="text-left">
            <div className="flex items-center space-x-2">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-slate-900 uppercase tracking-tight">
                TRH {plantName}
              </h3>
              <span className="text-2xl"><FlagImg code={country} size={28} /></span>
            </div>
            <div className="flex items-center text-slate-500 text-sm mt-1">
              <MapPin className="h-4 w-4 mr-1" />
              {getText('productionPlant')} – {plantLocation}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp className="h-6 w-6 text-slate-500" /> : <ChevronDown className="h-6 w-6 text-slate-500" />}
      </button>

      {/* Plant Content */}
      {expanded && (
        <div className="p-6">
          {/* Management System Certificates */}
          {managementCerts.length > 0 && (
            <div className="mb-10">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-1 h-6 bg-green-600"></div>
                <h4 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight">
                  {getText('managementSystem')}
                </h4>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {managementCerts.map((cert) => (
                  <div
                    key={cert.id}
                    className="bg-slate-50 border border-slate-200 p-5 flex"
                    data-testid={`mgmt-cert-${cert.id}`}
                  >
                    <div className="flex-shrink-0 mr-5">
                      <CertLogo type={cert.logo} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-heading text-base font-bold text-slate-900 uppercase tracking-tight">
                          {cert.name}
                        </h5>
                        {cert.scheme && (
                          <span className={`text-xs font-bold px-2 py-0.5 border ${getSchemeBadgeColor(cert.scheme)}`}>
                            {cert.scheme}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 text-sm mb-3 line-clamp-2">
                        {getDescription(cert.description)}
                      </p>
                      
                      <div className="space-y-1 text-xs mb-4">
                        <div className="flex">
                          <span className="font-medium text-slate-500 w-28">{getText('issuingBody')}:</span>
                          <span className="text-slate-700">{cert.issuing_body}</span>
                        </div>
                        <div className="flex">
                          <span className="font-medium text-slate-500 w-28">{getText('certNumber')}:</span>
                          <span className="font-mono text-slate-700">{cert.certificate_number}</span>
                        </div>
                        <div className="flex">
                          <span className="font-medium text-slate-500 w-28">{getText('validUntil')}:</span>
                          <span className="font-mono text-slate-700">{formatDate(cert.valid_until)}</span>
                        </div>
                      </div>
                      
                      <a
                        href={cert.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                        data-testid={`download-${cert.id}`}
                      >
                        <Download className="h-3 w-3" />
                        <span>{getText('downloadPdf')}</span>
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Product Certifications */}
          {productCerts.length > 0 && (
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-1 h-6 bg-green-600"></div>
                <h4 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight">
                  {getText('productCertifications')}
                </h4>
              </div>
              
              {/* Group by scheme */}
              {Object.entries(productCertsByScheme).map(([scheme, certs]) => (
                <div key={scheme} className="mb-6 last:mb-0">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className={`text-xs font-bold px-3 py-1 border ${getSchemeBadgeColor(scheme)}`}>
                      {scheme}
                    </span>
                    <span className="text-slate-400 text-sm">
                      ({certs.length} {certs.length === 1 ? 'certificate' : 'certificates'})
                    </span>
                  </div>
                  
                  <div className="grid gap-4">
                    {certs.map((cert) => (
                      <div
                        key={cert.id}
                        className="bg-white border border-slate-200 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
                        data-testid={`product-cert-${cert.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h5 className="font-heading text-sm font-bold text-slate-900 uppercase tracking-tight">
                              {cert.name}
                            </h5>
                            {cert.edition && (
                              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5">
                                {cert.edition}
                              </span>
                            )}
                          </div>
                          <p className="text-slate-600 text-sm mb-2">
                            {getDescription(cert.description)}
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                            <span><strong>{getText('issuingBody')}:</strong> {cert.issuing_body}</span>
                            <span><strong>{getText('certNumber')}:</strong> <code className="font-mono">{cert.certificate_number}</code></span>
                            {cert.standards && (
                              <span><strong>{getText('standards')}:</strong> {cert.standards.join(', ')}</span>
                            )}
                            <span><strong>{getText('validUntil')}:</strong> {formatDate(cert.valid_until)}</span>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <a
                            href={cert.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors"
                            data-testid={`view-cert-${cert.id}`}
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span>{getText('viewCertificate')}</span>
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const Quality = () => {
  const { t, language } = useLanguage();
  const [sevillaCerts, setSevillaCerts] = useState([]);
  const [liegeCerts, setLiegeCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCertificates = async () => {
    try {
      const [sevillaRes, liegeRes] = await Promise.all([
        axios.get(`${API}/certificates?plant=sevilla`),
        axios.get(`${API}/certificates?plant=liege`)
      ]);
      const prefixUrl = (cert) => ({
        ...cert,
        file_url: cert.file_url?.startsWith('/') ? `${BACKEND_URL}${cert.file_url}` : cert.file_url,
        file_url_nl: cert.file_url_nl?.startsWith('/') ? `${BACKEND_URL}${cert.file_url_nl}` : cert.file_url_nl,
      });
      setSevillaCerts(sevillaRes.data.certificates.map(prefixUrl));
      setLiegeCerts(liegeRes.data.certificates.map(prefixUrl));
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getText = (key) => {
    const texts = {
      qualityCertifications: { en: 'Quality & Certifications', es: 'Calidad y Certificaciones', fr: 'Qualité et Certifications', de: 'Qualität & Zertifizierungen', nl: 'Kwaliteit & Certificeringen' },
      introTitle: { en: 'European Quality Standards', es: 'Estándares de Calidad Europeos', fr: 'Normes de Qualité Européennes', de: 'Europäische Qualitätsstandards', nl: 'Europese Kwaliteitsnormen' },
      introText: { 
        en: 'TRH Steel operates under strict European quality standards. Quality is an integrated process covering management systems, production control and product certification. All certificates are organized by plant and product scope.',
        es: 'TRH Steel opera bajo estrictos estándares de calidad europeos. La calidad es un proceso integrado que abarca sistemas de gestión, control de producción y certificación de producto. Todos los certificados están organizados por planta y alcance de producto.',
        fr: 'TRH Steel opère selon des normes de qualité européennes strictes. La qualité est un processus intégré couvrant les systèmes de management, le contrôle de production et la certification des produits. Tous les certificats sont organisés par usine et portée de produit.',
        de: 'TRH Steel arbeitet nach strengen europäischen Qualitätsstandards. Qualität ist ein integrierter Prozess, der Managementsysteme, Produktionskontrolle und Produktzertifizierung umfasst. Alle Zertifikate sind nach Werk und Produktumfang organisiert.',
        nl: 'TRH Steel werkt volgens strenge Europese kwaliteitsnormen. Kwaliteit is een geïntegreerd proces dat managementsystemen, productiecontrole en productcertificering omvat. Alle certificaten zijn georganiseerd per fabriek en productomvang.'
      },
      ourPlants: { en: 'Our Plants', es: 'Nuestras Plantas', fr: 'Nos Usines', de: 'Unsere Werke', nl: 'Onze Fabrieken' },
    };
    return texts[key]?.[language] || texts[key]?.['en'] || key;
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="quality-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-slate-900 text-white" data-testid="quality-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-green-500 text-sm uppercase tracking-wider font-bold mb-4 block">
              {t('quality')}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-6">
              {getText('qualityCertifications')}
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed">
              {getText('introText')}
            </p>
          </div>
        </div>
      </section>

      {/* Quality Process Blocks */}
      <section className="py-16 md:py-24 bg-white border-b border-slate-200" data-testid="quality-blocks">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 uppercase tracking-tight">
              {getText('introTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityBlocks.map((block) => (
              <div
                key={block.titleKey}
                className="bg-slate-50 border border-slate-200 p-6 text-center"
                data-testid={`quality-block-${block.titleKey}`}
              >
                <div className="w-14 h-14 bg-slate-900 flex items-center justify-center mx-auto mb-4">
                  <block.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading text-base font-bold text-slate-900 uppercase tracking-tight mb-2">
                  {t(block.titleKey)}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t(block.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plant Certificates Hub */}
      <section className="py-16 md:py-24" data-testid="certificates-hub">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-10">
            <div className="w-1 h-8 bg-slate-900"></div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 uppercase tracking-tight">
              {getText('ourPlants')}
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin h-10 w-10 border-4 border-slate-900 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <>
              {/* TRH Sevilla */}
              <PlantSection
                plant="sevilla"
                plantName="Sevilla"
                plantLocation="Seville, Spain"
                country="ES"
                flag="ES"
                certificates={sevillaCerts}
                language={language}
                t={t}
              />

              {/* TRH Liège */}
              <PlantSection
                plant="liege"
                plantName="Liège"
                plantLocation="Liège, Belgium"
                country="BE"
                flag="BE"
                certificates={liegeCerts}
                language={language}
                t={t}
              />
            </>
          )}
        </div>
      </section>

      {/* Quality Assurance */}
      <section className="py-16 md:py-24 bg-slate-900 text-white" data-testid="quality-assurance">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-6">
                {t('everyMeterTested')}
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-6">
                {t('everyMeterTestedDesc')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { labelKey: 'tensileTesting', value: '100%' },
                  { labelKey: 'batchTraceability', value: '100%' },
                ].map((item) => (
                  <div key={item.labelKey} className="bg-slate-800 p-4">
                    <div className="font-heading text-2xl font-bold text-green-500">{item.value}</div>
                    <div className="text-sm text-slate-400">{t(item.labelKey)}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/images/site/hero-3.png`}
                alt="Quality Testing"
                className="w-full shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
